/**
 * "Hold Will" — strong human player, not superhuman.
 *
 * Constraints like a good player:
 * - Only the hand + Recall (never free full-deck cast)
 * - Chooses by verse reference (no mid-fight study delay)
 * - Runner adds reaction lag after speech (not frame-perfect)
 *
 * Priority: hold Will → mend damaged keeps → clear Will → chip crowds → sniper Fear → redeem → expand
 */

import { allLies } from "../game/board/board";
import { LAYER_ORDER, type LayerId } from "../game/board/types";
import { tiltBand } from "../game/board/tiltBands";
import { prayerPlaceIntent } from "./prayerPlace";
import type { DragIntent, Presence } from "../game/cards/types";
import type { Game } from "../game/Game";
import {
  dogmaDemand,
  dogmaIsCore,
  isDogmaPresence,
} from "../game/combat/dogma";
import { isLieRedeemable } from "../game/combat/redeemable";
import type { CardInstance, VerseDef } from "../game/cards/types";
import type { BotAction } from "./types";

function layerEnemyCount(game: Game, layer: LayerId): number {
  return game.board[layer].slots.filter((p) => p?.side === "enemy").length;
}

function layerHasPrayer(game: Game, layer: LayerId): boolean {
  return game.board[layer].slots.some((p) => p?.side === "player");
}

function darkestLayer(game: Game): LayerId {
  return LAYER_ORDER.reduce((best, id) =>
    game.board[id].tilt > game.board[best].tilt ? id : best,
  );
}

function countEnemies(game: Game): number {
  return allLies(game.board).length;
}

function willThreat(game: Game): boolean {
  return layerEnemyCount(game, "will") > 0;
}

function findCard(
  hand: CardInstance[],
  pred: (def: VerseDef) => boolean,
): CardInstance | undefined {
  return hand.find((c) => pred(c.def));
}

function isSoften(def: VerseDef): boolean {
  return def.kind === "instant" && def.snipeTier === "soften";
}

function isFearSniper(def: VerseDef): boolean {
  if (def.kind !== "instant") return false;
  if (def.snipeTier === "soften") return false;
  if (
    def.selector === "convertLie" ||
    def.selector === "self" ||
    def.selector === "empower" ||
    def.selector === "restorePrayer" ||
    def.selector === "gatherPrayers"
  ) {
    return false;
  }
  if (def.selector === "layerChip" || def.selector === "layerSlam") return false;
  if (def.selector === "slowLayer" || def.selector === "slowLie") return false;
  return (
    !!def.strongVs?.includes("fear") ||
    !!def.priorityTypes?.includes("fear") ||
    def.selector === "strongestLie" ||
    def.selector === "innermostLie" ||
    def.selector === "typePriority"
  );
}

function isEmpower(def: VerseDef): boolean {
  return def.kind === "instant" && def.selector === "empower";
}

function isRestorePrayer(def: VerseDef): boolean {
  return def.kind === "instant" && def.selector === "restorePrayer";
}

function isGather(def: VerseDef): boolean {
  return def.kind === "instant" && def.selector === "gatherPrayers";
}

function isInnermostSniper(def: VerseDef): boolean {
  return def.kind === "instant" && def.selector === "innermostLie";
}

function isLayerChip(def: VerseDef): boolean {
  return (
    def.kind === "instant" &&
    (def.selector === "layerChip" || def.selector === "layerSlam")
  );
}

function isRedeem(def: VerseDef): boolean {
  return def.kind === "instant" && def.selector === "convertLie";
}

function isStillLayer(def: VerseDef): boolean {
  return def.kind === "instant" && def.selector === "slowLayer";
}

function isRestoreIntegrity(def: VerseDef): boolean {
  return def.kind === "instant" && !!def.restoreIntegrity;
}

function isJoyField(def: VerseDef): boolean {
  return def.kind === "instant" && !!def.joyField;
}

function countFearLies(game: Game): number {
  return allLies(game.board).filter(
    (l) => l.presence.side === "enemy" && l.presence.lieType === "fear",
  ).length;
}

function chipLayer(def: VerseDef, game: Game): LayerId | null {
  if (!isLayerChip(def)) return null;
  if (def.lockLayer) return def.lockLayer;
  let best: LayerId | null = null;
  let bestN = 0;
  for (const L of LAYER_ORDER) {
    let n = 0;
    for (const p of game.board[L].slots) {
      if (p?.side !== "enemy") continue;
      if (def.chipOnlyTypes?.length) {
        if (!p.lieType || !def.chipOnlyTypes.includes(p.lieType)) continue;
      }
      n++;
    }
    if (n > bestN) {
      bestN = n;
      best = L;
    }
  }
  return bestN >= 2 ? best : null;
}

function placeableTarget(
  game: Game,
  def: VerseDef,
  prefer: LayerId,
): DragIntent | null {
  if (!def.placeable) return null;
  return prayerPlaceIntent(game, prefer, def);
}

function lieIntent(game: Game, preferLayer?: LayerId): DragIntent | null {
  const lies = allLies(game.board);
  if (!lies.length) return null;
  let pool = lies;
  if (preferLayer) {
    const f = lies.filter((l) => l.layer === preferLayer);
    if (f.length) pool = f;
  }
  pool.sort((a, b) => {
    const li = LAYER_ORDER.indexOf(b.layer) - LAYER_ORDER.indexOf(a.layer);
    if (li !== 0) return li;
    return b.presence.level - a.presence.level;
  });
  const t = pool[0]!;
  return {
    layer: t.layer,
    slot: t.slot,
    presenceId: t.presence.instanceId,
  };
}

/** Best redeemable lie (answered at least once), prefer Despair then high level / inner. */
function redeemIntent(game: Game): DragIntent | null {
  const ready = allLies(game.board).filter((l) =>
    isLieRedeemable(l.presence),
  );
  if (!ready.length) return null;
  ready.sort((a, b) => {
    const ad = a.presence.lieType === "despair" ? 1 : 0;
    const bd = b.presence.lieType === "despair" ? 1 : 0;
    if (bd !== ad) return bd - ad;
    const dl = b.presence.level - a.presence.level;
    if (dl !== 0) return dl;
    return LAYER_ORDER.indexOf(b.layer) - LAYER_ORDER.indexOf(a.layer);
  });
  const t = ready[0]!;
  return {
    layer: t.layer,
    slot: t.slot,
    presenceId: t.presence.instanceId,
  };
}

/** Full-strength lie we want to soft for redeem (prefer Despair). */
function chipSetupForRedeem(game: Game): {
  layer: LayerId;
  slot: number;
  presence: Presence;
} | null {
  const full = allLies(game.board).filter(
    (l) => l.presence.side === "enemy" && !isLieRedeemable(l.presence),
  );
  if (!full.length) return null;
  full.sort((a, b) => {
    const ad = a.presence.lieType === "despair" ? 1 : 0;
    const bd = b.presence.lieType === "despair" ? 1 : 0;
    if (bd !== ad) return bd - ad;
    const dl = b.presence.level - a.presence.level;
    if (dl !== 0) return dl;
    return LAYER_ORDER.indexOf(b.layer) - LAYER_ORDER.indexOf(a.layer);
  });
  const t = full[0]!;
  return { layer: t.layer, slot: t.slot, presence: t.presence };
}

export function holdWillDecide(game: Game): BotAction {
  if (game.status !== "playing") return { type: "wait", reason: "not playing" };
  if (game.userPaused) return { type: "wait", reason: "paused" };
  if (game.paused) return { type: "wait", reason: "paused" };
  if (game.isProclaiming()) return { type: "wait", reason: "proclaiming" };

  const hand = game.hand;
  if (hand.length === 0) {
    return { type: "wait", reason: "empty hand" };
  }

  const enemies = countEnemies(game);
  const willEnemies = layerEnemyCount(game, "will");
  const willHeld = layerHasPrayer(game, "will");
  const integrity = game.resources.integrity;

  // 1. Will emergency
  if (willEnemies > 0) {
    const inner = findCard(hand, isInnermostSniper);
    if (inner) {
      return {
        type: "proclaim",
        instanceId: inner.instanceId,
        intent: lieIntent(game, "will"),
        reason: "Will threat → innermost",
      };
    }
    const sniper = findCard(hand, isFearSniper);
    if (sniper) {
      return {
        type: "proclaim",
        instanceId: sniper.instanceId,
        intent: lieIntent(game, "will"),
        reason: "Will threat → sniper",
      };
    }
  }

  // 1b. Dogma peel / wipe / lay aside
  {
    const dogma = allLies(game.board).find((l) =>
      isDogmaPresence(l.presence),
    );
    if (dogma?.presence.dogma) {
      const demand = dogmaDemand(dogma.presence.dogma);
      const peel = findCard(
        hand,
        (d) =>
          d.category === demand &&
          !!d.answerLevels &&
          d.selector !== "layerChip",
      );
      if (peel) {
        return {
          type: "proclaim",
          instanceId: peel.instanceId,
          intent: {
            layer: dogma.layer,
            slot: dogma.slot,
            presenceId: dogma.presence.instanceId,
          },
          reason: `Dogma wants ${demand}`,
        };
      }
      if (dogmaIsCore(dogma.presence.dogma)) {
        const knife = findCard(hand, (d) => !!d.castDown);
        if (knife) {
          return {
            type: "proclaim",
            instanceId: knife.instanceId,
            intent: null,
            reason: "Cast down Dogma core",
          };
        }
      }
    }
    const confusions = allLies(game.board).filter(
      (l) => l.presence.lieType === "confusion",
    );
    if (confusions.length) {
      const wipe = findCard(hand, (d) => !!d.wipeConfusion);
      if (wipe) {
        return {
          type: "proclaim",
          instanceId: wipe.instanceId,
          intent: null,
          reason: "Wipe Confusion",
        };
      }
    }
    const lay = findCard(hand, (d) => !!d.layAside);
    if (lay && hand.length >= 3) {
      const brick = hand.find(
        (c) =>
          c.instanceId !== lay.instanceId &&
          (c.def.selector === "empower" ||
            c.def.selector === "convertLie" ||
            c.def.selector === "gatherPrayers"),
      );
      if (brick) {
        return {
          type: "proclaim",
          instanceId: lay.instanceId,
          intent: {
            layer: "mind",
            slot: 0,
            layAsideInstanceId: brick.instanceId,
          },
          reason: "Lay aside brick",
        };
      }
    }
  }

  // 2. Hold Will
  if (!willHeld) {
    const prayer = findCard(hand, (d) => d.kind === "placeable");
    if (prayer) {
      const intent = placeableTarget(game, prayer.def, "will");
      if (intent) {
        return {
          type: "proclaim",
          instanceId: prayer.instanceId,
          intent,
          reason: "Abide Will",
        };
      }
    }
  }

  // 2b. Mend a damaged keep before keep-hunters finish the wall
  {
    const restore = findCard(hand, isRestorePrayer);
    if (restore) {
      const damaged: {
        layer: LayerId;
        slot: number;
        presenceId: string;
        level: number;
      }[] = [];
      for (const L of LAYER_ORDER) {
        game.board[L].slots.forEach((p, slot) => {
          if (p?.side === "player" && p.level > 0 && p.level < p.maxLevel) {
            damaged.push({
              layer: L,
              slot,
              presenceId: p.instanceId,
              level: p.level,
            });
          }
        });
      }
      if (damaged.length) {
        damaged.sort(
          (a, b) =>
            a.level - b.level ||
            LAYER_ORDER.indexOf(b.layer) - LAYER_ORDER.indexOf(a.layer),
        );
        const t = damaged[0]!;
        return {
          type: "proclaim",
          instanceId: restore.instanceId,
          intent: {
            layer: t.layer,
            slot: t.slot,
            presenceId: t.presenceId,
          },
          reason: "Restore Keep",
        };
      }
    }
  }

  // 2c. Outer weather flipped — Still the darkest row that can march
  {
    const still = findCard(hand, isStillLayer);
    if (still && (!willThreat(game) || willHeld)) {
      const flipped = LAYER_ORDER.filter((L) => {
        if (L === "will") return false;
        return (
          game.board[L].tilt >= tiltBand(L).lieStrong &&
          layerEnemyCount(game, L) > 0
        );
      });
      if (flipped.length) {
        const L = flipped[0]!;
        return {
          type: "proclaim",
          instanceId: still.instanceId,
          intent: { layer: L, slot: 1 },
          reason: `Still flipped ${L}`,
        };
      }
    }
  }

  // 3. Integrity / Joy when Fear swarm or low integrity
  if (integrity < 40) {
    const restore = findCard(hand, isRestoreIntegrity);
    if (restore) {
      return {
        type: "proclaim",
        instanceId: restore.instanceId,
        intent: null,
        reason: "Low integrity",
      };
    }
  }
  {
    const fears = countFearLies(game);
    if (fears >= 2) {
      const joy = findCard(hand, isJoyField);
      if (joy) {
        return {
          type: "proclaim",
          instanceId: joy.instanceId,
          intent: null,
          reason: "Joy vs Fear swarm",
        };
      }
    }
  }

  // 4. Redeem setup: if we hold Redeem but only full-strength lies, soften/chip first
  {
    const redeem = findCard(hand, isRedeem);
    if (redeem && (!willThreat(game) || willHeld)) {
      const ready = redeemIntent(game);
      if (!ready) {
        const setup = chipSetupForRedeem(game);
        if (setup) {
          const intent = {
            layer: setup.layer,
            slot: setup.slot,
            presenceId: setup.presence.instanceId,
          };
          // Prefer targeted Soften (−1) over layer chip / full snipe
          const soften = findCard(hand, isSoften);
          if (soften) {
            return {
              type: "proclaim",
              instanceId: soften.instanceId,
              intent,
              reason: "Soften to open Redeem",
            };
          }
          const chip = findCard(hand, (d) => {
            if (!isLayerChip(d)) return false;
            if (d.lockLayer && d.lockLayer !== setup.layer) return false;
            if (d.chipOnlyTypes?.length) {
              return (
                !!setup.presence.lieType &&
                d.chipOnlyTypes.includes(setup.presence.lieType)
              );
            }
            return true;
          });
          if (chip) {
            return {
              type: "proclaim",
              instanceId: chip.instanceId,
              intent: { layer: setup.layer, slot: 1 },
              reason: `Chip to open Redeem (${setup.layer})`,
            };
          }
          // Sniper answer also opens redeem
          const sniper =
            findCard(hand, isInnermostSniper) ?? findCard(hand, isFearSniper);
          if (sniper && setup.presence.level >= 2) {
            return {
              type: "proclaim",
              instanceId: sniper.instanceId,
              intent,
              reason: "Answer to open Redeem",
            };
          }
        }
      }
    }
  }

  // 5. Layer chip on crowds (2+)
  const chips = hand
    .filter((c) => isLayerChip(c.def))
    .map((c) => {
      const L = chipLayer(c.def, game);
      const n = L
        ? game.board[L].slots.filter((p) => {
            if (p?.side !== "enemy") return false;
            if (c.def.chipOnlyTypes?.length) {
              return !!p.lieType && c.def.chipOnlyTypes.includes(p.lieType);
            }
            return true;
          }).length
        : 0;
      return { c, L, n };
    })
    .filter((x) => x.L && x.n >= 2)
    .sort((a, b) => b.n - a.n);

  if (chips[0]) {
    return {
      type: "proclaim",
      instanceId: chips[0].c.instanceId,
      intent: { layer: chips[0].L!, slot: 1 },
      reason: `Chip ${chips[0].L} (${chips[0].n})`,
    };
  }

  // 6. Redeem answered lies (prefer Despair / high level)
  if (!willThreat(game) || willHeld) {
    const ready = redeemIntent(game);
    if (ready) {
      const redeem = findCard(hand, isRedeem);
      if (redeem) {
        const lie = game.board[ready.layer].slots[ready.slot];
        return {
          type: "proclaim",
          instanceId: redeem.instanceId,
          intent: ready,
          reason: `Redeem ${lie?.name ?? "lie"} (${lie ? lie.level : "?"}<max)`,
        };
      }
    }
  }

  // 7. Finish residuals (level I) on Will/Heart before new full claims
  {
    const residuals = allLies(game.board).filter(
      (l) =>
        l.presence.level <= 1 &&
        (l.layer === "will" || l.layer === "heart"),
    );
    if (residuals.length) {
      residuals.sort(
        (a, b) =>
          LAYER_ORDER.indexOf(b.layer) - LAYER_ORDER.indexOf(a.layer),
      );
      const sniper =
        findCard(hand, isInnermostSniper) ?? findCard(hand, isFearSniper);
      if (sniper) {
        const t = residuals[0]!;
        return {
          type: "proclaim",
          instanceId: sniper.instanceId,
          intent: {
            layer: t.layer,
            slot: t.slot,
            presenceId: t.presence.instanceId,
          },
          reason: "Finish residual",
        };
      }
    }
  }

  // 8a. Gather residuals into one keep (2+ Prayer I, or I feeding a damaged host)
  {
    const gather = findCard(hand, isGather);
    if (gather) {
      const prayers: {
        layer: LayerId;
        slot: number;
        presenceId: string;
        level: number;
        maxLevel: number;
      }[] = [];
      for (const L of LAYER_ORDER) {
        game.board[L].slots.forEach((p, slot) => {
          if (p?.side === "player" && p.level > 0) {
            prayers.push({
              layer: L,
              slot,
              presenceId: p.instanceId,
              level: p.level,
              maxLevel: p.maxLevel,
            });
          }
        });
      }
      const residualI = prayers.filter((x) => x.level === 1);
      if (residualI.length >= 2) {
        // Host: closest to Will among those below max with room and a spare I
        const hosts = prayers
          .filter((h) => {
            if (h.level >= h.maxLevel) return false;
            const otherI = residualI.length - (h.level === 1 ? 1 : 0);
            return otherI >= 1;
          })
          .sort(
            (a, b) =>
              LAYER_ORDER.indexOf(b.layer) - LAYER_ORDER.indexOf(a.layer) ||
              a.level - b.level,
          );
        const t = hosts[0];
        if (t) {
          return {
            type: "proclaim",
            instanceId: gather.instanceId,
            intent: {
              layer: t.layer,
              slot: t.slot,
              presenceId: t.presenceId,
            },
            reason: "Gather residual Prayers",
          };
        }
      }
    }
  }

  // 8. Restore damaged Keep (worn Prayer)
  {
    const restore = findCard(hand, isRestorePrayer);
    if (restore) {
      const damaged: {
        layer: LayerId;
        slot: number;
        presenceId: string;
        level: number;
      }[] = [];
      for (const L of LAYER_ORDER) {
        game.board[L].slots.forEach((p, slot) => {
          if (p?.side === "player" && p.level > 0 && p.level < p.maxLevel) {
            damaged.push({
              layer: L,
              slot,
              presenceId: p.instanceId,
              level: p.level,
            });
          }
        });
      }
      if (damaged.length) {
        damaged.sort((a, b) => a.level - b.level);
        const t = damaged[0]!;
        return {
          type: "proclaim",
          instanceId: restore.instanceId,
          intent: {
            layer: t.layer,
            slot: t.slot,
            presenceId: t.presenceId,
          },
          reason: "Restore Keep",
        };
      }
    }
  }

  // 9. Empower before a sniper when charges empty and board has work
  if (
    game.empowerCharges <= 0 &&
    enemies > 0 &&
    (!willThreat(game) || willHeld)
  ) {
    const emp = findCard(hand, isEmpower);
    const follow =
      findCard(hand, isFearSniper) ??
      findCard(hand, isInnermostSniper) ??
      findCard(hand, isLayerChip);
    if (emp && follow) {
      return {
        type: "proclaim",
        instanceId: emp.instanceId,
        intent: null,
        reason: "Empower setup",
      };
    }
  }

  // 10. Fear snipers (prefer highest level / innermost)
  if (allLies(game.board).some((l) => l.presence.lieType === "fear")) {
    const sniper =
      findCard(hand, isInnermostSniper) ?? findCard(hand, isFearSniper);
    if (sniper) {
      return {
        type: "proclaim",
        instanceId: sniper.instanceId,
        intent: lieIntent(game, willEnemies > 0 ? "will" : undefined),
        reason: "Fear → sniper",
      };
    }
  }

  // 11. Any answer
  if (enemies > 0) {
    const anyAnswer = findCard(
      hand,
      (d) =>
        d.kind === "instant" &&
        d.selector !== "self" &&
        d.selector !== "slowLayer" &&
        d.selector !== "convertLie" &&
        d.selector !== "empower" &&
        d.selector !== "restorePrayer" &&
        d.selector !== "gatherPrayers" &&
        !isLayerChip(d),
    );
    if (anyAnswer) {
      return {
        type: "proclaim",
        instanceId: anyAnswer.instanceId,
        intent: lieIntent(game),
        reason: "Answer lie",
      };
    }
    const still = findCard(hand, isStillLayer);
    if (still) {
      return {
        type: "proclaim",
        instanceId: still.instanceId,
        intent: { layer: darkestLayer(game), slot: 1 },
        reason: "Still layer",
      };
    }
  }

  // 9. Expand
  const prayer = findCard(hand, (d) => d.kind === "placeable");
  if (prayer) {
    const prefer =
      layerEnemyCount(game, "heart") >= layerEnemyCount(game, "mind")
        ? "heart"
        : "mind";
    const intent = placeableTarget(
      game,
      prayer.def,
      willHeld ? prefer : "will",
    );
    if (intent) {
      return {
        type: "proclaim",
        instanceId: prayer.instanceId,
        intent,
        reason: `Abide ${intent.layer}`,
      };
    }
  }

  // 10. Light chip
  for (const c of hand) {
    if (!isLayerChip(c.def)) continue;
    const L = c.def.lockLayer ?? darkestLayer(game);
    if (layerEnemyCount(game, L) >= 1) {
      return {
        type: "proclaim",
        instanceId: c.instanceId,
        intent: { layer: L, slot: 1 },
        reason: `Light chip ${L}`,
      };
    }
  }

  // Hand refills on play — no manual Recall
  return { type: "wait", reason: "no legal play" };
}
